import dayjs from 'dayjs';
import { IngredientDto, ProductDto } from '@kotipizzagroup/kotipizza-products-api-client';

/** Master-ingredient is enabled for use. */
export const isIngredientActive = (ingredient: IngredientDto): boolean => Boolean(ingredient.isActive);

/** Master-product is enabled for use. */
export const isProductActive = (product: ProductDto): boolean => product.active === true;

/** Not archived. Works on any record carrying an optional isArchived flag. */
export const isNotArchived = <T extends { isArchived?: boolean }>(item: T): boolean => !item.isArchived;

/** Active AND not archived — the common "sellable master record" gate. */
export const isIngredientSelectable = (ingredient: IngredientDto): boolean =>
  isIngredientActive(ingredient) && isNotArchived(ingredient);

export const isProductSelectable = (product: ProductDto): boolean =>
  isProductActive(product) && isNotArchived(product);

/**
 * Keep only the first occurrence of each warehouseItemId; items without one are always kept.
 * Several ingredients can map to the same physical warehouse item (e.g. "Ananas" and
 * "Ananas (uunin jälkeen)"), so this collapses them to a single row.
 */
export const dedupeByWarehouseItemId = <T extends { warehouseItemId?: string | null }>(items: T[]): T[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (!item.warehouseItemId) return true;
    if (seen.has(item.warehouseItemId)) return false;
    seen.add(item.warehouseItemId);
    return true;
  });
};

/**
 * True while an out-of-stock entry is still in effect: open-ended entries (no scheduledUntil)
 * are always in effect; scheduled ones stay until their scheduledUntil timestamp passes (now).
 */
export const isScheduleInEffect = <T extends { scheduledUntil?: string | null }>(item: T): boolean =>
  !item.scheduledUntil || dayjs(item.scheduledUntil).isAfter(dayjs());
